import type { ControlRuleResult } from './controlRule.ts'
import { checkControlRule } from './controlRule.ts'
import type { SheetRow } from './typeColumn.ts'
import { normalizeHeader } from './vocabulary.ts'

const POOL_HEADER =
  /^(sequencing|seq|pcr|extraction|library|lib|sample|probe|proben)?(pool|plate|platte|run|lauf|batch|charge|library|lib|bibliothek)(?:s|n|es|en)?(id|name|nr|no|number|nummer)?$/

const NOUN_LABEL: Record<string, string> = {
  pool: 'Pool',
  plate: 'Plate',
  platte: 'Platte',
  run: 'Run',
  lauf: 'Lauf',
  batch: 'Batch',
  charge: 'Charge',
  library: 'Library',
  lib: 'Library',
  bibliothek: 'Bibliothek'
}

/* A pool column groups rows; plates mirror control placement more closely than runs do. */
const NOUN_PRIORITY = ['pool', 'plate', 'platte', 'library', 'lib', 'bibliothek', 'batch', 'charge', 'run', 'lauf']

/** Matches the backend's pool label limit. */
const MAX_POOL_NAME = 64

export const poolHeaderNoun = (header: string): string | undefined => POOL_HEADER.exec(normalizeHeader(header))?.[2]

/** Group identity of a pool value: case, separators and leading zeros do not tell pools apart. */
export const normalizePoolValue = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/\d+/g, (digits) => String(Number.parseInt(digits, 10)))
    .replace(/[^\p{L}\p{N}]/gu, '')

/**
 * The pool name a sheet value gives: readable identifiers as written, a bare number prefixed with the
 * column's noun ("3" in a plate column is "Plate 3"). Undefined when the value is too long to be a label.
 */
export const poolNameFor = (value: string, column: string): string | undefined => {
  const trimmed = value.trim().replace(/\s+/g, ' ')
  if (/^\d+$/.test(trimmed)) {
    const noun = poolHeaderNoun(column)
    return `${noun ? NOUN_LABEL[noun] : 'Pool'} ${Number.parseInt(trimmed, 10)}`
  }
  return trimmed.length > 0 && trimmed.length <= MAX_POOL_NAME ? trimmed : undefined
}

export type PoolColumnContext = {
  /** Whether a row is a negative control, as resolved by the caller's precedence. */
  isControl: (rowIndex: number) => boolean
  /** Identity of the item a row stands for, so a control listed under several pools counts as one. */
  itemKey: (rowIndex: number) => string
  /** The order's current pool names; a column holding exactly these is the order's own pool column. */
  knownPoolNames?: readonly string[]
}

export type PoolGroup = {
  /** {@link normalizePoolValue} of the value. */
  key: string
  /** First spelling seen. */
  value: string
  name?: string
  rows: number[]
  /** Item keys of the group's controls, deduplicated. */
  controls: string[]
}

export type PoolVerdict = 'pools' | 'empty' | 'single' | 'unique' | 'too-many-groups' | 'control-rule'

export type PoolColumnEvidence = {
  column: string
  /** The pool-ish noun the header names, when it does. */
  headerNoun?: string
  groups: PoolGroup[]
  /** Rows without a value; they keep their pool. */
  emptyRows: number[]
  filledRows: number
  /** Most groups a column may form and still group rows: about half the rows with a value. */
  maxGroups: number
  verdict: PoolVerdict
  controlRule: ControlRuleResult
  /** Whether the values can serve as pool names: every group has one and no two collide. */
  namesUsable: boolean
  matchesKnownPools: boolean
}

export type PoolColumnDetection = PoolColumnEvidence & {
  auto: boolean
  /** Whether a user may still pick it by hand: a column that groups nothing cannot assign pools. */
  usable: boolean
  reason: string
}

export const analyzePoolColumn = (column: string, rows: readonly SheetRow[], context: PoolColumnContext): PoolColumnEvidence => {
  const groups = new Map<string, PoolGroup & { controlSet: Set<string> }>()
  const emptyRows: number[] = []
  rows.forEach((row, index) => {
    const value = (row[column] ?? '').trim()
    const key = normalizePoolValue(value)
    if (key === '') {
      emptyRows.push(index)
      return
    }
    let group = groups.get(key)
    if (!group) {
      group = { key, value, name: poolNameFor(value, column), rows: [], controls: [], controlSet: new Set() }
      groups.set(key, group)
    }
    group.rows.push(index)
    if (context.isControl(index)) {
      group.controlSet.add(context.itemKey(index))
    }
  })

  const list: PoolGroup[] = [...groups.values()].map(({ controlSet, ...group }) => ({ ...group, controls: [...controlSet] }))
  const filled = rows.length - emptyRows.length
  const maxGroups = Math.max(2, Math.ceil(filled / 2))
  const controlRule = checkControlRule(list)
  const names = list.map((group) => group.name?.toLowerCase())
  const known = new Set((context.knownPoolNames ?? []).map(normalizePoolValue))

  const verdict: PoolVerdict =
    list.length === 0
      ? 'empty'
      : list.length === 1
        ? 'single'
        : list.length === filled
          ? 'unique'
          : list.length > maxGroups
            ? 'too-many-groups'
            : controlRule.holds
              ? 'pools'
              : 'control-rule'

  return {
    column,
    headerNoun: poolHeaderNoun(column),
    groups: list,
    emptyRows,
    filledRows: filled,
    maxGroups,
    verdict,
    controlRule,
    namesUsable: names.every((name) => name !== undefined) && new Set(names).size === names.length,
    matchesKnownPools: list.length > 0 && known.size > 0 && list.every((group) => known.has(group.key))
  }
}

const reasonFor = (evidence: PoolColumnEvidence): string => {
  switch (evidence.verdict) {
    case 'empty':
      return 'The column is empty.'
    case 'single':
      return 'Every row has the same value: one pool, nothing to assign.'
    case 'unique':
      return 'Every row has its own value, so it groups nothing.'
    case 'too-many-groups':
      return `${evidence.groups.length} groups for ${evidence.filledRows} rows; a pool column groups about two rows or more per pool.`
    case 'control-rule':
      return evidence.controlRule.withoutControls.length > 1
        ? `${evidence.controlRule.withoutControls.length} groups have no negative control; at most one may.`
        : 'Some groups share exactly the same negative controls, so they would not be told apart.'
    default:
      return 'Every group has its own set of negative controls.'
  }
}

export const judgePoolColumn = (column: string, rows: readonly SheetRow[], context: PoolColumnContext): PoolColumnDetection => {
  const evidence = analyzePoolColumn(column, rows, context)
  const usable = evidence.verdict !== 'empty' && evidence.verdict !== 'unique'
  if (!evidence.headerNoun) {
    return { ...evidence, auto: false, usable, reason: `The header does not name pools, plates or runs. ${reasonFor(evidence)}` }
  }
  /* the order's own pool names (an export round trip) are its pools already, whatever their controls */
  const auto = evidence.verdict === 'pools' || (evidence.verdict === 'control-rule' && evidence.matchesKnownPools)
  return { ...evidence, auto, usable, reason: reasonFor(evidence) }
}

/** The column to assign pools from, when one qualifies by its header, its grouping and its controls. */
export const detectPoolColumn = (
  headers: readonly string[],
  rows: readonly SheetRow[],
  context: PoolColumnContext,
  exclude: readonly string[] = []
): PoolColumnDetection | undefined => {
  const rank = (detection: PoolColumnDetection) => NOUN_PRIORITY.indexOf(detection.headerNoun ?? '')
  return headers
    .filter((header) => !exclude.includes(header))
    .map((header) => judgePoolColumn(header, rows, context))
    .filter((detection) => detection.auto)
    .sort((a, b) => rank(a) - rank(b))[0]
}
